import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Bot,
  Clock,
  Database,
  Download,
  Gauge,
  PauseCircle,
  PlayCircle,
  Radio,
  RefreshCw,
  Server,
  ShieldCheck,
  TrendingUp,
  WifiOff,
  Zap,
} from 'lucide-react';
import '../styles/HLTrendBotPanel.css';

const BOT_API = '/api/hl-trend-bot';
const LEGACY_STORAGE_KEYS = [
  ['spiralarc', 'hl', 'trend', 'bot', 'url'].join('_'),
  ['spiralarc', 'hl', 'trend', 'bot', 'secret'].join('_'),
];
// Last good /health snapshot. Tab switches unmount this panel, and the status
// round trip crosses four systems (Vercel proxy → auth verify → Railway bot →
// exchange), so without a cache every remount blanks to "Connecting" for
// seconds. Stale-while-revalidate: render the snapshot immediately, refresh
// in the background, and show the "as of" stamp so staleness is visible.
const SNAPSHOT_KEY = 'spiralarc_hl_trend_bot_snapshot';
const FETCH_TIMEOUT_MS = 12000;

function readSnapshot() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || 'null');
    return parsed && parsed.health ? parsed : null;
  } catch {
    return null;
  }
}

function writeSnapshot(health) {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ health, ts: Date.now() }));
  } catch {
    /* storage unavailable — panel still works, just without remount cache */
  }
}

const TABLES = [
  { id: 'trades', label: 'Trades' },
  { id: 'orders', label: 'Orders' },
  { id: 'events', label: 'Activity' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'errors', label: 'Errors' },
];

const TABLE_COLUMNS = {
  trades: ['id', 'symbol', 'side', 'size', 'entry_px', 'stop_px', 'exit_px', 'pnl_usd', 'status', 'note'],
  orders: ['id', 'symbol', 'kind', 'side', 'size', 'px', 'status', 'detail'],
  events: ['id', 'level', 'component', 'event_type', 'message'],
  alerts: ['alert_id', 'event', 'symbol', 'status', 'result'],
  errors: ['id', 'component', 'error_type', 'message'],
};

function formatBotError(error) {
  const message = error?.message || 'Unable to reach HL Trend Bot';
  if (/401|dashboard login/i.test(message)) {
    return 'Dashboard session expired. Sign in again with an admin account.';
  }
  if (/403|admin access/i.test(message)) {
    return 'Admin access is required for the HL Trend Bot tab.';
  }
  if (/503|not configured/i.test(message)) {
    return 'HL Trend Bot backend configuration is missing on the dashboard server.';
  }
  if (/502|failed to fetch|networkerror|load failed|timed out|abort|unable to reach/i.test(message)) {
    return 'The dashboard backend could not reach the HL Trend Bot service.';
  }
  return message;
}

function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return `${formatNumber(num * 100, decimals)}%`;
}

function formatSeconds(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  const value = Number(seconds);
  if (!Number.isFinite(value)) return '-';
  if (value < 60) return `${Math.round(value)}s`;
  if (value < 3600) return `${Math.round(value / 60)}m`;
  return `${Math.round(value / 3600)}h`;
}

function formatToken(value) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value).replace(/_/g, ' ');
}

function renderCell(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return Math.abs(value) >= 100 ? formatNumber(value) : formatNumber(value, 4);
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  return text.length > 96 ? `${text.slice(0, 96)}...` : text;
}

function getColumns(activeTable, rows) {
  const preferred = TABLE_COLUMNS[activeTable] || [];
  const seen = new Set(preferred);
  const extras = rows.flatMap(row => Object.keys(row)).filter(key => {
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...preferred, ...extras].filter(key => rows.some(row => key in row)).slice(0, 10);
}

export default function HLTrendBotPanel() {
  const [health, setHealth] = useState(() => readSnapshot()?.health || null);
  const [rows, setRows] = useState([]);
  const [activeTable, setActiveTable] = useState('trades');
  const [loading, setLoading] = useState(false);
  const [controlLoading, setControlLoading] = useState('');
  const [error, setError] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [lastChecked, setLastChecked] = useState(() => {
    const snapshot = readSnapshot();
    return snapshot?.ts ? new Date(snapshot.ts) : null;
  });

  const counts = health?.counts || {};
  const columns = useMemo(() => getColumns(activeTable, rows), [activeTable, rows]);
  const lastPoll = Array.isArray(health?.last_poll_summary) ? health.last_poll_summary[0] : null;
  const symbolLabel = (health?.symbols || []).join(', ') || lastPoll?.coin || lastPoll?.symbol || '-';
  const isArmed = Boolean(health?.trading_enabled);
  const automationLabel = health?.automation_mode === 'self_compute'
    ? 'Self-compute'
    : formatToken(health?.automation_mode || 'Connecting');
  const signalSource = health?.signal_source || 'Hyperliquid candles + funding';
  const dataSource = health?.data_source || signalSource;
  const webhookLabel = health?.webhook_required
    ? 'Webhook required'
    : (health?.webhook_ingest_enabled ? 'Webhook ingest on' : 'No webhook needed');
  const riskMultiplier = lastPoll?.risk_multiplier || 1;
  const fundingRank = lastPoll?.funding_rank;
  const errorCount = counts.errors || 0;
  const dataWarningLabel = errorCount ? 'Historical fetch warnings' : 'No data warnings';
  const feeRateLabel = health?.taker_fee_rate
    ? `${formatPercent(health.taker_fee_rate, 3)} taker`
    : 'Fee estimate pending';
  // live v19 signal internals published by the bot's poll cycle (health.signal,
  // keyed by coin) — the data behind the Pine status panel
  const signal = health?.signal
    ? (health.signal[symbolLabel] || Object.values(health.signal)[0] || null)
    : null;
  const strengthBlocks = signal?.Y != null
    ? Math.round(Math.min(Math.abs(signal.Y), 1) * 8)
    : 0;
  const strengthMeter = '█'.repeat(strengthBlocks) + '░'.repeat(8 - strengthBlocks);
  // a closed bar should never be more than one interval + grace behind;
  // derive the interval from the bot config instead of assuming 4h
  const INTERVAL_SECONDS = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400 };
  const intervalSec = INTERVAL_SECONDS[health?.candle_interval] || 14400;
  const candleStale = signal?.candle_age_sec != null && signal.candle_age_sec > intervalSec + 900;
  const emptyState = useMemo(() => {
    if (error) {
      return {
        title: 'Bot service unavailable',
        detail: 'The dashboard backend could not load the managed HL Trend Bot status.',
      };
    }
    if (loading) {
      return {
        title: 'Refreshing bot status',
        detail: 'Fetching the latest runtime snapshot from the managed bot.',
      };
    }
    return {
      title: 'Waiting for bot status',
      detail: 'Refresh to load the managed HL Trend Bot runtime.',
    };
  }, [error, loading]);

  const botFetch = useCallback(async (path, options = {}) => {
    // Abort hung round trips: a request that never resolves would otherwise
    // pin `loading` and block every later 15s refresh cycle.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(`${BOT_API}${path}`, {
        ...options,
        signal: controller.signal,
        credentials: 'include',
        headers: {
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...(options.headers || {}),
        },
      });
    } finally {
      clearTimeout(timer);
    }

    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const message = typeof payload === 'object'
        ? (payload.error || payload.detail || 'Bot request failed')
        : (payload || 'Bot request failed');
      throw new Error(`${response.status} ${message}`);
    }

    return payload;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setReviewError('');

    try {
      const healthPayload = await botFetch('/health');
      setHealth(healthPayload);
      setLastChecked(new Date());
      writeSnapshot(healthPayload);
    } catch (err) {
      setError(formatBotError(err));
      setLoading(false);
      return;
    }

    try {
      const reviewPayload = await botFetch(`/review?table=${encodeURIComponent(activeTable)}&limit=12`);
      setRows(reviewPayload.rows || []);
    } catch (err) {
      setRows([]);
      setReviewError(formatBotError(err));
    } finally {
      setLoading(false);
    }
  }, [activeTable, botFetch]);

  useEffect(() => {
    localStorage.removeItem(LEGACY_STORAGE_KEYS[0]);
    sessionStorage.removeItem(LEGACY_STORAGE_KEYS[1]);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, [refresh]);

  const runControl = async (action) => {
    setControlLoading(action);
    setError('');
    setReviewError('');
    try {
      await botFetch(`/control?action=${encodeURIComponent(action)}`, { method: 'POST' });
      await refresh();
    } catch (err) {
      setError(formatBotError(err));
    } finally {
      setControlLoading('');
    }
  };

  const downloadCsv = async () => {
    setReviewError('');
    try {
      const response = await fetch(`${BOT_API}/logs.csv?table=${encodeURIComponent(activeTable)}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(`${response.status} ${message}`);
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `hl-trend-bot-${activeTable}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      setReviewError(formatBotError(err));
    }
  };

  return (
    <div className="hlbot-panel">
      {/* ============================================================
          FEATURE: HL Trend Bot managed execution service
          ACCESS: Admin only (Stage 1)
          TODO: Keep hidden from traders until explicitly enabled
          LAST UPDATED: 2026-07-04
          ============================================================ */}
      <section className={`hlbot-hero ${isArmed ? 'is-armed' : 'is-paused'}`}>
        <div className="hlbot-hero-main">
          <div className="hlbot-state-line">
            <span className="hlbot-state-dot" />
            <span>{health ? (isArmed ? 'Armed' : 'Paused') : 'Connecting'}</span>
            <span>{health?.testnet ? 'Testnet' : health ? 'Mainnet' : 'Network check'}</span>
            {lastChecked && (
              <span className="hlbot-updated" title="When the runtime snapshot on screen was last fetched">
                as of {lastChecked.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="hlbot-hero-title">
            <span className="hlbot-icon"><Bot size={19} /></span>
            <div>
              <h2>HL Trend v19</h2>
              <p>{health?.strategy || 'TREND v19 ANCHOR'}</p>
            </div>
          </div>
          <div className="hlbot-hero-chips">
            <span><Radio size={13} /> {automationLabel}</span>
            <span><Database size={13} /> {dataSource}</span>
            <span><ShieldCheck size={13} /> Admin API</span>
            <span className={health?.webhook_ingest_enabled ? 'chip-warn' : ''}>{webhookLabel}</span>
          </div>
        </div>

        <div className="hlbot-hero-side">
          <div className="hlbot-hero-equity">
            <span>Equity</span>
            <strong>${formatNumber(health?.equity)}</strong>
            <small>{symbolLabel}</small>
          </div>
          <div className="hlbot-hero-actions">
            <button type="button" className="hlbot-icon-btn" onClick={refresh} disabled={loading} title="Refresh status">
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
            </button>
            <button
              type="button"
              className="hlbot-btn danger"
              onClick={() => runControl('pause')}
              disabled={!health || !isArmed || controlLoading === 'pause'}
            >
              <PauseCircle size={15} />
              {controlLoading === 'pause' ? 'Pausing' : 'Pause'}
            </button>
            <button
              type="button"
              className="hlbot-btn success"
              onClick={() => runControl('resume')}
              disabled={!health || isArmed || controlLoading === 'resume'}
            >
              <PlayCircle size={15} />
              {controlLoading === 'resume' ? 'Resuming' : 'Resume'}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="hlbot-alert">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {!health ? (
        <section className="hlbot-empty">
          <WifiOff size={22} />
          <strong>{emptyState.title}</strong>
          <span>{emptyState.detail}</span>
        </section>
      ) : (
        <>
          <section className="hlbot-command-grid">
            <div className="hlbot-signal-card">
              <div className="hlbot-card-top">
                <span><TrendingUp size={15} /> Current Signal</span>
                <strong>{lastPoll?.coin || lastPoll?.symbol || symbolLabel}</strong>
              </div>
              <div className="hlbot-signal-body">
                <strong>{formatToken(lastPoll?.want || lastPoll?.target || 'flat')}</strong>
                <span>{lastPoll?.reason || 'Waiting for closed-bar decision'}</span>
              </div>
              <div className="hlbot-signal-metrics">
                <span>Action <strong>{formatToken(lastPoll?.action || 'synced')}</strong></span>
                <span>Risk x<strong>{formatNumber(riskMultiplier, 2)}</strong></span>
                <span>Funding <strong>{formatPercent(fundingRank, 1)}</strong></span>
              </div>
            </div>

            <div className="hlbot-telemetry-grid">
              <div className="hlbot-metric">
                <span><Activity size={14} /> Run State</span>
                <strong className={isArmed ? 'state-live' : 'state-paused'}>{isArmed ? 'ARMED' : 'PAUSED'}</strong>
                <small>{health.testnet ? 'Testnet guardrail' : 'Mainnet'}</small>
              </div>
              <div className="hlbot-metric">
                <span><Clock size={14} /> Poll Age</span>
                <strong>{formatSeconds(health.last_poll_age_sec)}</strong>
                <small>{health.candle_interval} / every {formatSeconds(health.poll_seconds)}</small>
              </div>
              <div className="hlbot-metric">
                <span><Server size={14} /> Runtime</span>
                <strong>{formatSeconds(health.uptime_sec)}</strong>
                <small>{lastChecked ? lastChecked.toLocaleTimeString() : '-'}</small>
              </div>
              <div className={`hlbot-metric ${errorCount ? 'metric-warn' : ''}`}>
                <span><AlertTriangle size={14} /> Data Warnings</span>
                <strong>{errorCount}</strong>
                <small>{dataWarningLabel}</small>
              </div>
            </div>
          </section>

          {/* ============================================================
              FEATURE: Strategy internals (live v19 signal state)
              ACCESS: Admin only (Stage 1)
              Mirrors the Pine LEDGERGLASS status panel: trend/strength,
              breakout triggers, veto, anchor, data freshness — served by
              the bot's poll cycle via /health.signal. LAST UPDATED: 2026-08-30
              ============================================================ */}
          {signal && signal.warmup_ok && (
            <section className="hlbot-telemetry-grid hlbot-brain">
              <div className="hlbot-metric">
                <span><TrendingUp size={14} /> Trend</span>
                <strong className={signal.D > 0 ? 'state-live' : signal.D < 0 ? 'state-paused' : ''}>
                  {signal.D > 0 ? 'BULL ↑' : signal.D < 0 ? 'BEAR ↓' : 'NEUTRAL →'}
                </strong>
                <small>{signal.trend_age_bars} bars in state</small>
              </div>
              <div className="hlbot-metric" title={`Composite Y = ${signal.Y} (entry threshold ±0.05)`}>
                <span><Gauge size={14} /> Strength</span>
                <strong className="hlbot-meter">{strengthMeter}</strong>
                <small>Y {signal.Y > 0 ? '+' : ''}{formatNumber(signal.Y, 3)} · need ±0.05</small>
              </div>
              <div className="hlbot-metric">
                <span><TrendingUp size={14} /> Long Trigger</span>
                <strong>${formatNumber(signal.long_trigger)}</strong>
                <small>{formatNumber(signal.dist_to_long_pct, 2)}% away{signal.D <= 0 ? ' · trend blocks longs' : ''}</small>
              </div>
              <div className="hlbot-metric">
                <span><TrendingUp size={14} /> Short Trigger</span>
                <strong>${formatNumber(signal.short_trigger)}</strong>
                <small>{formatNumber(signal.dist_to_short_pct != null ? Math.abs(signal.dist_to_short_pct) : null, 2)}% away{signal.D >= 0 ? ' · trend blocks shorts' : ''}</small>
              </div>
              <div className={`hlbot-metric ${(signal.veto_blocking_longs || signal.veto_blocking_shorts) ? 'metric-warn' : ''}`}>
                <span><ShieldCheck size={14} /> Carry Veto</span>
                <strong>
                  {!signal.carry_veto_enabled ? 'OFF'
                    : signal.veto_blocking_longs ? 'BLOCKING LONGS'
                    : signal.veto_blocking_shorts ? 'BLOCKING SHORTS'
                    : `ok · ${formatPercent(signal.funding_rank, 0)}`}
                </strong>
                <small>blocks &gt;{formatPercent(signal.veto_upper_q, 0)} / &lt;{formatPercent(signal.veto_lower_q, 0)}</small>
              </div>
              <div className="hlbot-metric">
                <span><Zap size={14} /> Anchor</span>
                <strong>
                  {signal.anchor_up_active ? '▲ level crossed' : signal.anchor_down_active ? '▼ level crossed' : 'idle'}
                </strong>
                <small>boost ×{formatNumber(health.anchor_boost_multiplier ?? 1.25, 2)} on entry</small>
              </div>
              <div className={`hlbot-metric ${candleStale ? 'metric-warn' : ''}`}>
                <span><Database size={14} /> Candle Data</span>
                <strong>{candleStale ? 'STALE' : 'FRESH'}</strong>
                <small>closed bar {formatSeconds(signal.candle_age_sec)} old</small>
              </div>
              <div className="hlbot-metric">
                <span><Radio size={14} /> Funding Data</span>
                <strong>{signal.funding_age_sec != null ? formatSeconds(signal.funding_age_sec) : '-'}</strong>
                <small>last funding print age</small>
              </div>
            </section>
          )}

          <section className="hlbot-policy-grid">
            <div className="hlbot-policy">
              <span><Gauge size={14} /> Risk</span>
              <strong>{formatNumber((health.risk_fraction || 0) * 100, 2)}%</strong>
              <small>Max {health.max_leverage}x</small>
            </div>
            <div className="hlbot-policy">
              <span><Zap size={14} /> Anchor</span>
              <strong>x{formatNumber(health.anchor_boost_multiplier || 1, 2)}</strong>
              <small>Long boost only</small>
            </div>
            <div className="hlbot-policy">
              <span><Database size={14} /> Carry</span>
              <strong>{health.carry_veto_enabled ? 'ON' : 'OFF'}</strong>
              <small>{formatNumber((health.carry_lower_q || 0) * 100, 0)}-{formatNumber((health.carry_upper_q || 0) * 100, 0)} rank</small>
            </div>
            <div className="hlbot-policy">
              <span><Radio size={14} /> Automation</span>
              <strong>{health.webhook_required ? 'ALERTS' : 'AUTO'}</strong>
              <small>{webhookLabel}</small>
            </div>
            <div className="hlbot-policy">
              <span><ShieldCheck size={14} /> Fees</span>
              <strong>{formatToken(health.fee_model || 'base perp')}</strong>
              <small>{feeRateLabel}</small>
            </div>
          </section>

          <section className="hlbot-review">
            <div className="hlbot-review-header">
              <div>
                <span>Audit Log</span>
                <strong>{TABLES.find(table => table.id === activeTable)?.label || 'Rows'}</strong>
              </div>
              <button type="button" className="hlbot-btn export" onClick={downloadCsv} disabled={!rows.length}>
                <Download size={14} />
                CSV
              </button>
            </div>

            <div className="hlbot-review-tabs">
              {TABLES.map((table) => (
                <button
                  type="button"
                  key={table.id}
                  className={`hlbot-review-tab ${activeTable === table.id ? 'active' : ''}`}
                  onClick={() => setActiveTable(table.id)}
                >
                  {table.label}
                  <span>{counts[table.id] || 0}</span>
                </button>
              ))}
            </div>

            {reviewError && (
              <div className="hlbot-alert hlbot-alert-inline">
                <AlertTriangle size={16} />
                <span>{reviewError}</span>
              </div>
            )}

            <div className="hlbot-table-wrap">
              {rows.length === 0 ? (
                <div className="hlbot-table-empty">No rows</div>
              ) : (
                <table className="hlbot-table">
                  <thead>
                    <tr>
                      {columns.map((column) => (
                        <th key={column}>{column.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr key={row.id || row.alert_id || rowIndex}>
                        {columns.map((column) => (
                          <td key={column}>{renderCell(row[column])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
